import { inject } from '@angular/core';
import { NavigationError, Router } from '@angular/router';

import { UiHelpersService } from '../shared/ui/services/ui-helpers/ui-helpers.service';

/**
 * Context available to navigation error handlers.
 * Add more dependencies as needed.
 */
export interface NavigationErrorHandlerContext {
  router: Router;
  uiHelpers: UiHelpersService;
}

/**
 * Navigation error handler function signature.
 * Returns `true` if the error was handled and should stop propagation to other handlers.
 * Returns `false` to continue to the next handler.
 */
export type NavigationErrorHandler = (
  navError: NavigationError,
  context: NavigationErrorHandlerContext
) => boolean;

/**
 * Creates a matcher that checks whether the error message contains any of the
 * given texts.
 */
export const matchNavigationError = (
  ...messages: string[]
): ((navError: NavigationError) => boolean) => {
  return navError => {
    const error = navError.error;
    const errorMessage = error instanceof Error ? error.message : String(error);
    return messages.some(message => errorMessage.includes(message));
  };
};

// ============================================================================
// Error Handlers
// ============================================================================

export const CHUNK_LOAD_ERROR_MESSAGE =
  'Something went wrong loading this page. Please try again, or reach out to our dev team if the issue persists.';

/**
 * Messages browsers raise when a lazy route's chunk fails to download
 * (Chrome/Edge, Firefox and Safari respectively). Browsers give no error code
 * for this, so the message text is the only reliable signal.
 */
export const isChunkLoadError = matchNavigationError(
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed'
);

/**
 * Browsers cache a failed dynamic import for the life of the page, so the
 * route stays broken until a full reload. The router still reports the error
 * (console and Airbrake), but the user would see nothing, so tell them to
 * reload.
 */
export const chunkLoadErrorHandler: NavigationErrorHandler = (
  navError,
  context
) => {
  if (isChunkLoadError(navError)) {
    context.uiHelpers.errorSnackbar(CHUNK_LOAD_ERROR_MESSAGE);
    return true;
  }
  return false;
};

// Add more handlers here as needed:
// export const someOtherErrorHandler: NavigationErrorHandler = (navError, context) => { ... };

// ============================================================================
// Default Handler Chain
// ============================================================================

/**
 * Default list of navigation error handlers, processed in order.
 * First handler to return `true` stops the chain.
 */
export const DEFAULT_NAVIGATION_ERROR_HANDLERS: NavigationErrorHandler[] = [
  chunkLoadErrorHandler,
  // Add more handlers here in order of priority
];

/**
 * Creates the callback for `withNavigationErrorHandler`, which runs each
 * navigation error through the handler chain. Errors no handler takes keep the
 * router's default behaviour.
 *
 * @param handlers - Array of handlers (defaults to DEFAULT_NAVIGATION_ERROR_HANDLERS)
 */
export const createNavigationErrorHandler = (
  handlers: NavigationErrorHandler[] = DEFAULT_NAVIGATION_ERROR_HANDLERS
): ((navError: NavigationError) => void) => {
  return navError => {
    const context: NavigationErrorHandlerContext = {
      router: inject(Router),
      uiHelpers: inject(UiHelpersService),
    };
    for (const handler of handlers) {
      if (handler(navError, context)) {
        break;
      }
    }
  };
};
